/**
 * components/LifecycleModal.jsx
 * Shared popup modal that shows the full lifecycle audit trail for a drawing.
 * Used by Dashboard.jsx (uploader) and ReviewerDashboard.jsx (reviewer).
 *
 * Props:
 *   drawingId   {number}   DB id of the drawing
 *   drawingName {string}   Fallback display name while data loads
 *   onClose     {()=>void} Called when the user dismisses the modal
 */

import React, { useEffect, useState } from 'react';
import API from '../services/api';

/* ── UTC parse helper — backend sends naive UTC strings without 'Z' ── */
const toUTC = iso => iso ? new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z') : null;

/* ── Date formatter: "April 3rd, 2026 at 2:45 PM" ───────────── */
function ordinalSuffix(n) {
  const s = ['th', 'st', 'nd', 'rd'], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}
export function fmtLifecycleDate(iso) {
  if (!iso) return '—';
  const d    = toUTC(iso);
  const mon  = new Intl.DateTimeFormat('en-US', { month: 'long'    }).format(d);
  const yr   = new Intl.DateTimeFormat('en-US', { year: 'numeric'  }).format(d);
  const time = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).format(d);
  return `${mon} ${ordinalSuffix(d.getDate())}, ${yr} at ${time}`;
}

/* ── Duration formatter: 125s → "2 min 5 sec" ───────────────── */
function fmtDuration(seconds) {
  if (seconds == null || seconds < 0) return '—';
  if (seconds < 60) return `${seconds} sec`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s > 0 ? `${m} min ${s} sec` : `${m} min`;
}

/* ── Stage colour map ────────────────────────────────────────── */
const STAGE_DOTS = {
  uploaded:'#3B82F6', queued:'#7C3AED', processing:'#F97316', completed:'#10B981',
  failed:'#EF4444', shared:'#2563EB', re_shared:'#D97706', self_approved:'#059669',
  reviewer_opened:'#22C55E', approved:'#10B981', rejected:'#EF4444',
  plan_edited:'#8B5CF6', review_deleted:'#DC2626',
};
const STAGE_BADGE = {
  uploaded:       { bg:'#EFF6FF', color:'#1D4ED8' },
  queued:         { bg:'#F5F3FF', color:'#6D28D9' },
  processing:     { bg:'#FFF7ED', color:'#C2410C' },
  completed:      { bg:'#ECFDF5', color:'#065F46' },
  failed:         { bg:'#FEF2F2', color:'#991B1B' },
  shared:         { bg:'#EFF6FF', color:'#1E40AF' },
  re_shared:      { bg:'#FFF7ED', color:'#92400E' },
  self_approved:  { bg:'#ECFDF5', color:'#065F46' },
  reviewer_opened:{ bg:'#F0FDF4', color:'#14532D' },
  approved:       { bg:'#ECFDF5', color:'#065F46' },
  rejected:       { bg:'#FEF2F2', color:'#991B1B' },
  plan_edited:    { bg:'#F5F3FF', color:'#6D28D9' },
  review_deleted: { bg:'#FEF2F2', color:'#991B1B' },
};

/* ── Stages that should only appear once in the timeline ─────── */
const DEDUPE_STAGES = new Set(['queued', 'processing', 'plan_edited']);

/* ── Modal skeleton (shimmer) ─────────────────────────────────── */
const MSK = {
  base: {
    background: 'linear-gradient(90deg, #F5F0EA 25%, #E8E2DC 50%, #F5F0EA 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.4s ease-in-out infinite',
    borderRadius: 6,
    flexShrink: 0,
  },
};
const MSk = ({ w, h = 13, r = 6 }) => (
  <div style={{ ...MSK.base, width: w, height: h, borderRadius: r }} />
);

function ModalSkeleton() {
  const rowBg = (i) => ({ background: i % 2 === 0 ? '#fff' : '#FDFCFB' });
  return (
    <div style={{ padding: '18px 22px 22px' }}>
      {/* Summary table skeleton */}
      <div style={{ ...MSK.base, width: 65, height: 10, marginBottom: 10 }} />
      <div style={{ border: '1px solid #EDE8E1', borderRadius: 8, overflow: 'hidden', marginBottom: 20 }}>
        <div style={{ background: '#FAFAF9', padding: '8px 12px', borderBottom: '1px solid #EDE8E1', display: 'flex', gap: 36 }}>
          <MSk w={50} h={10} /><MSk w={100} h={10} /><MSk w={85} h={10} />
        </div>
        {[0, 1, 2].map(i => (
          <div key={i} style={{ ...rowBg(i), padding: '10px 12px', borderBottom: '1px solid #F5F0EA', display: 'flex', gap: 36, alignItems: 'center' }}>
            <MSk w={75} /><MSk w={115} /><MSk w={90} />
          </div>
        ))}
      </div>

      {/* Timeline skeleton */}
      <div style={{ ...MSK.base, width: 110, height: 10, marginBottom: 10 }} />
      <div style={{ border: '1px solid #EDE8E1', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ background: '#FAFAF9', padding: '8px 12px', borderBottom: '1px solid #EDE8E1', display: 'flex', gap: 24 }}>
          <MSk w={55} h={10} /><MSk w={80} h={10} /><MSk w={60} h={10} /><MSk w={80} h={10} />
        </div>
        {[0, 1, 2, 3, 4].map(i => (
          <div key={i} style={{ ...rowBg(i), padding: '10px 12px', borderBottom: '1px solid #F5F0EA', display: 'flex', gap: 24, alignItems: 'center' }}>
            <MSk w={76} h={20} r={999} />
            <MSk w={95} />
            <MSk w={68} />
            <MSk w={84} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function LifecycleModal({ drawingId, drawingName, onClose }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [err,     setErr]     = useState(null);

  useEffect(() => {
    if (!drawingId) return;
    setLoading(true); setData(null); setErr(null);
    API.getLifecycle(drawingId)
      .then(d  => { setData(d);                           setLoading(false); })
      .catch(e => { setErr(e?.error || 'Failed to load'); setLoading(false); });
  }, [drawingId]);

  /* Deduplicate queued/processing — keep only first occurrence */
  const deduped = (() => {
    if (!data?.events) return [];
    const seen = new Set();
    return data.events.filter(ev => {
      if (!DEDUPE_STAGES.has(ev.stage)) return true;
      if (seen.has(ev.stage)) return false;
      seen.add(ev.stage);
      return true;
    });
  })();

  return (
    <div style={lm.overlay} onClick={onClose}>
      <div style={lm.box} onClick={e => e.stopPropagation()}>

        {/* ── Header ── */}
        <div style={lm.header}>
          <div>
            <div style={lm.title}>{data?.display_name || drawingName || 'Lifecycle'}</div>
            {data && <div style={lm.sub}>Drawing #{drawingId}</div>}
          </div>
          <button style={lm.closeBtn} onClick={onClose}>✕</button>
        </div>

        {loading && <ModalSkeleton />}
        {err     && <div style={{ ...lm.center, color: '#991B1B' }}>{err}</div>}

        {data && !loading && (
          <div style={lm.body}>

            {/* ── Summary table ── */}
            <div style={lm.sectionLabel}>Summary</div>
            <table style={lm.tbl}>
              <thead>
                <tr>
                  <th style={lm.th}>Detail</th>
                  <th style={lm.th}>Name / Value</th>
                  <th style={lm.th}>Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {/* Created by */}
                <tr>
                  <td style={lm.tdLabel}>Created by</td>
                  <td style={lm.td}>{data.created_by || '—'}</td>
                  <td style={lm.tdDate}>{fmtLifecycleDate(data.created_at)}</td>
                </tr>

                {/* Scan duration */}
                <tr>
                  <td style={lm.tdLabel}>Scan Duration</td>
                  <td style={lm.td}>
                    {data.processing_duration_seconds != null
                      ? fmtDuration(data.processing_duration_seconds)
                      : '—'}
                  </td>
                  <td style={lm.tdDate}>—</td>
                </tr>

                {/* Inspection plan edited / not edited */}
                <tr>
                  <td style={lm.tdLabel}>Inspection Plan</td>
                  <td style={lm.td}>
                    {data.plan_edited_by
                      ? <span style={{ color: '#6D28D9', fontWeight: 600 }}>Edited by {data.plan_edited_by}</span>
                      : <span style={{ color: '#9C9490' }}>Not Edited</span>}
                  </td>
                  <td style={lm.tdDate}>{fmtLifecycleDate(data.plan_edited_at)}</td>
                </tr>

                {/* Reviewed by / Approved by / Rejected by */}
                {(data.reviewed_by || data.reviewed_at) && (
                  <tr>
                    <td style={lm.tdLabel}>
                      {data.review_outcome === 'approved'      ? 'Approved by' :
                       data.review_outcome === 'self_approved' ? 'Self-approved by' :
                       data.review_outcome === 'rejected'      ? 'Rejected by' : 'Reviewed by'}
                    </td>
                    <td style={lm.td}>
                      <div>{data.reviewed_by || '—'}</div>
                      {data.reviewer_comment && (
                        <div style={lm.commentQuote}>"{data.reviewer_comment}"</div>
                      )}
                    </td>
                    <td style={lm.tdDate}>{fmtLifecycleDate(data.reviewed_at)}</td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* ── Event timeline ── */}
            <div style={{ ...lm.sectionLabel, marginTop: 20 }}>Event Timeline</div>
            {deduped.length === 0 ? (
              <p style={{ color: '#9C9490', fontSize: 13 }}>No events recorded yet.</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={lm.tbl}>
                  <thead>
                    <tr>
                      <th style={lm.th}>Stage</th>
                      <th style={lm.th}>Details</th>
                      <th style={lm.th}>Actor</th>
                      <th style={lm.th}>Timestamp</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deduped.map((ev, i) => {
                      const dot   = STAGE_DOTS[ev.stage]  || '#9CA3AF';
                      const badge = STAGE_BADGE[ev.stage] || { bg: '#F3F4F6', color: '#374151' };
                      const hasComment = ev.metadata?.comments;
                      const hasError   = ev.metadata?.error;
                      return (
                        <tr key={ev.id || i} style={{ background: i % 2 === 0 ? '#fff' : '#FDFCFB' }}>
                          <td style={lm.td}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot, flexShrink: 0 }} />
                              <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: badge.bg, color: badge.color }}>
                                {ev.label}
                              </span>
                            </span>
                          </td>
                          <td style={{ ...lm.td, maxWidth: 220 }}>
                            {ev.stage === 'processing' && data.processing_duration_seconds != null && (
                              <div style={{ fontSize: 11, color: '#C2410C', marginBottom: hasComment || hasError ? 4 : 0 }}>
                                Duration: {fmtDuration(data.processing_duration_seconds)}
                              </div>
                            )}
                            {(ev.stage === 'shared' || ev.stage === 're_shared') && ev.metadata?.reviewer_emails?.length > 0 && (
                              <div style={{ fontSize: 11, color: '#1E40AF', marginBottom: hasComment || hasError ? 4 : 0 }}>
                                Sent to: {ev.metadata.reviewer_emails.join(', ')}
                              </div>
                            )}
                            {hasComment && (
                              <div style={lm.commentQuote}>"{hasComment}"</div>
                            )}
                            {hasError && (
                              <div style={{ fontSize: 11, color: '#991B1B', fontStyle: 'italic' }}>
                                {hasError}
                              </div>
                            )}
                            {ev.stage !== 'processing' && !(ev.stage === 'shared' || ev.stage === 're_shared') && !hasComment && !hasError && (
                              <span style={{ color: '#C4BDB8', fontSize: 12 }}>—</span>
                            )}
                            {ev.stage === 'processing' && data.processing_duration_seconds == null && !hasComment && !hasError && (
                              <span style={{ color: '#C4BDB8', fontSize: 12 }}>—</span>
                            )}
                            {(ev.stage === 'shared' || ev.stage === 're_shared') && !ev.metadata?.reviewer_emails?.length && !hasComment && !hasError && (
                              <span style={{ color: '#C4BDB8', fontSize: 12 }}>—</span>
                            )}
                          </td>
                          <td style={lm.td}>
                            <span style={{ fontSize: 13, color: ev.actor_role === 'system' ? '#9C9490' : '#2C2825' }}>
                              {ev.actor_name || '—'}
                            </span>
                            {ev.actor_role && ev.actor_role !== 'system' && (
                              <span style={{ fontSize: 11, color: '#9C9490', marginLeft: 4 }}>
                                ({ev.actor_role})
                              </span>
                            )}
                          </td>
                          <td style={{ ...lm.tdDate, whiteSpace: 'nowrap' }}>
                            {fmtLifecycleDate(ev.created_at)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Styles ──────────────────────────────────────────────────── */
const lm = {
  overlay:     { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  box:         { background: '#fff', borderRadius: 12, width: '100%', maxWidth: 760, maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' },
  header:      { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '18px 22px 14px', borderBottom: '1px solid #EDE8E1' },
  title:       { fontSize: 16, fontWeight: 700, color: '#2C2825' },
  sub:         { fontSize: 12, color: '#9C9490', marginTop: 2 },
  closeBtn:    { background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#9C9490', padding: '2px 6px', lineHeight: 1 },
  body:        { overflowY: 'auto', padding: '18px 22px 22px', flex: 1 },
  center:      { padding: 32, textAlign: 'center', color: '#9C9490', fontSize: 13 },
  sectionLabel:{ fontSize: 11, fontWeight: 700, color: '#9C9490', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 },
  tbl:         { width: '100%', borderCollapse: 'collapse', border: '1px solid #EDE8E1', borderRadius: 8, overflow: 'hidden', fontSize: 13 },
  th:          { padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#9C9490', background: '#FAFAF9', borderBottom: '1px solid #EDE8E1', textTransform: 'uppercase', letterSpacing: '0.05em' },
  td:          { padding: '9px 12px', verticalAlign: 'middle', borderBottom: '1px solid #F5F0EA' },
  tdLabel:     { padding: '9px 12px', fontWeight: 600, color: '#2C2825', borderBottom: '1px solid #F5F0EA', background: '#FDFCFB', whiteSpace: 'nowrap' },
  tdDate:      { padding: '9px 12px', color: '#6B6360', fontSize: 12, borderBottom: '1px solid #F5F0EA' },
  commentQuote:{ marginTop: 4, fontSize: 12, color: '#6B4E3D', fontStyle: 'italic', background: '#FFF8F0', borderLeft: '3px solid #F97316', paddingLeft: 8, paddingTop: 3, paddingBottom: 3, borderRadius: 2 },
};
